import { useEffect, useState } from 'react';
import type { HistoryEntry, HistoryKind } from '@flashpoint/site-dna';
import { onHistorySnapshot } from '../../lib/firestore';
import { fullDateTime, relativeTime } from '../../lib/dateFmt';

const KIND_COLOR: Record<HistoryKind, string> = {
  status_change: '#06b6d4',
  wizard_field: '#94a3b8',
  asset_upload: '#22c55e',
  provision_step: '#f59e0b',
  vfs_write: '#a855f7',
  note: '#64748b',
};

interface Props {
  leadId: string;
}

export function HistoryTab({ leadId }: Props) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);

  useEffect(() => onHistorySnapshot(leadId, setEntries, { limit: 200 }), [leadId]);

  if (entries === null) {
    return <div className="text-sm text-nm-muted">Loading history...</div>;
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-nm-border p-10 text-center text-sm text-nm-muted">
        No history entries yet. Status changes, wizard edits, asset uploads, and provisioning steps
        will appear here in real time.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => {
        const color = KIND_COLOR[entry.kind] ?? '#94a3b8';
        return (
          <div
            key={entry.id}
            className="fp-card !p-3 text-sm"
            style={{ boxShadow: `inset 3px 0 0 0 ${color}` }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: `${color}22`, color }}
                >
                  {entry.kind.replace('_', ' ')}
                </span>
                {entry.actorEmail && (
                  <span className="text-[11px] text-nm-muted">{entry.actorEmail}</span>
                )}
              </div>
              <span className="text-[11px] text-nm-muted" title={fullDateTime(entry.ts)}>
                {relativeTime(entry.ts)}
              </span>
            </div>
            {Object.keys(entry.payload).length > 0 && (
              <pre className="mt-2 overflow-x-auto rounded bg-nm-bg/60 p-2 text-[11px] text-nm-ink">
                {JSON.stringify(entry.payload, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
